import { Component, ElementRef, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy, inject, input, viewChild } from '@angular/core';
import { Consumption } from '@eadsysteme/energyefficiencyclient-v2';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { SessionService } from '../../../services/session.service';
import { DateUtil } from '../../../util/DateUtil';
import { SnackbarService } from '../../../services/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { Icons } from '../../../util/Icons';
import { NumberUtil } from '../../../util/NumberUtil';
import { fadeInUp400ms } from '../../../../@vex/animations/fade-in-up.animation';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_MONTHYEAR_FORMATS } from '../../../util/material-dayjs-adapter';
import dayjs from 'dayjs';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { ThemeService } from '../../../services/theme.service';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { DefaultClassDirective, DefaultStyleDirective } from '@ngbracket/ngx-layout/extended';
import { MatIcon } from '@angular/material/icon';
import { NgTooltipModule } from '@silmar/ng-tooltip';
import { BarChartModule } from '@swimlane/ngx-charts';
import CostTypeEnum = Consumption.CostTypeEnum;

@Component({
	selector: 'vex-chart-display-per-energytype',
	templateUrl: './chart-display-per-energytype.component.html',
	styleUrl: './chart-display-per-energytype.component.scss',
	animations: [fadeInUp400ms],
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		[NumberUtil],
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_MONTHYEAR_FORMATS },
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslocoDirective, NgClass, DefaultClassDirective, NgStyle, DefaultStyleDirective, MatIcon, NgTooltipModule, BarChartModule, NgTemplateOutlet],
})
export class ChartDisplayPerEnergytypeComponent implements OnInit, OnChanges {
	private translocoService = inject(TranslocoService);
	private themeService = inject(ThemeService);
	layoutService = inject(LayoutService);
	sessionService = inject(SessionService);
	private translocoLocaleService = inject(TranslocoLocaleService);
	dateUtilService = inject(DateUtil);
	dialog = inject(MatDialog);
	snackBar = inject(SnackbarService);
	icons = inject(Icons);
	numberUtil = inject(NumberUtil);

	readonly consumption = input<Consumption>(undefined);
	isMobile = false;
	protected readonly CostTypeEnum = CostTypeEnum;
	readonly chartComponent = viewChild<ElementRef>('chartComponent');
	// chart Config
	diagramSize: [number, number] = [550, 250];
	showXAxis = true;
	showYAxis = true;
	showYAxisLabel = true;
	customColors = [];
	public yAxisTickFormattingFn = this.yAxisTickFormatting.bind(this);
	public xAxisTickFormattingFn = this.yAxisTickFormatting.bind(this);
	chartDataHeating: any;
	chartDataWarmwater: any;
	chartDataCooling: any;
	chartDataColdwater: any;
	chartDataHeatingWarmwater: any;
	currentConsumptionName = '';
	lastYearConsumptionName = '';
	lastMonthConsumptionName = '';
	primaryRgbColor = '#5c77ff';
	grey = '#b1b1b1';
	lightGrey = '#dedede';
	colorScheme = {
		domain: [this.lightGrey, this.grey, this.primaryRgbColor],
	};

	readonly date = input<any>(undefined);
	readingDate: any;

	constructor() {
		this.primaryRgbColor = this.themeService.getPrimaryColorFromCssVar();
		this.numberUtil._locale = this.translocoLocaleService.getLocale();
	}

	ngOnInit(): void {
		this.readingDate = dayjs(this.date());
		this.setSizeIfMobile();
		this.primaryRgbColor = this.themeService.getPrimaryColorFromCssVar();
		this.colorScheme.domain = [this.lightGrey, this.grey, this.primaryRgbColor];
	}

	ngOnChanges(changes: SimpleChanges) {
		this.readingDate = dayjs(this.date());
		this.currentConsumptionName = '';
		this.lastYearConsumptionName = '';
		this.lastMonthConsumptionName = '';
		this.fillCharts();
		this.addCustomColors();
	}

	getTooltipByCostType(costType: CostTypeEnum) {
		switch (costType) {
			case CostTypeEnum.HEATING:
				return this.translocoService.translate('uvi.toolTipHeating');
			case CostTypeEnum.WARMWATER:
				return this.translocoService.translate('uvi.toolTipWarmwater');
			case CostTypeEnum.COLDWATER:
				return this.translocoService.translate('uvi.toolTipColdwater');
			case CostTypeEnum.COOLING:
				return this.translocoService.translate('uvi.toolTipCooling');
			case CostTypeEnum.HEATING_WARMWATER:
				return this.translocoService.translate('uvi.toolTipHeatingWarmwater');
		}
	}

	private fillCharts() {
		switch (this.consumption().costType) {
			case CostTypeEnum.HEATING:
				this.chartDataHeating = this.fillChartDataArray();
				break;
			case CostTypeEnum.WARMWATER:
				this.chartDataWarmwater = this.fillChartDataArray();
				break;
			case CostTypeEnum.COLDWATER:
				this.chartDataColdwater = this.fillChartDataArray();
				break;
			case CostTypeEnum.COOLING:
				this.chartDataCooling = this.fillChartDataArray();
				break;
			case CostTypeEnum.HEATING_WARMWATER:
				this.chartDataHeatingWarmwater = this.fillChartDataArray();
				break;
		}
	}

	roundConsumptionValuesByCostType(energyValue: Consumption): string {
		if (CostTypeEnum.COLDWATER !== energyValue.costType) {
			let value = this.scaleByCostType(energyValue.currentConsumptionPeriod.totalConsumptionAsKwh, energyValue.costType);
			value = this.replaceDecimalSeparatorByLocale(value);
			return value + ' ' + this.translocoService.translate('enum.KWH');
		} else {
			let value = this.scaleByCostType(energyValue.currentConsumptionPeriod.totalConsumption, energyValue.costType);
			value = this.replaceDecimalSeparatorByLocale(value);
			return value + ' ' + this.translocoService.translate('enum.CBM');
		}
	}

	scaleByCostType(value: number, costType: Consumption.CostTypeEnum) {
		return costType === CostTypeEnum.COLDWATER ? this.numberUtil.roundConsumptionValues(value, 2) : this.numberUtil.roundConsumptionValues(value, 0);
	}

	formatDataLabel(value: number, costType: CostTypeEnum) {
		if (value === undefined || value === null) {
			return '';
		} else {
			let label = this.scaleByCostType(value, costType);
			label = this.replaceDecimalSeparatorByLocale(label);
			return CostTypeEnum.COLDWATER === costType ? label + ' m³' : label + ' kWh';
		}
	}

	protected replaceDecimalSeparatorByLocale(label: string) {
		if ('de' === this.translocoService.getActiveLang()) {
			label = label.replace('.', ',');
		}
		return label;
	}

	private addCustomColors() {
		this.customColors.push({ name: this.lastYearConsumptionName, value: this.lightGrey });
		this.customColors.push({ name: this.lastMonthConsumptionName, value: this.grey });
		this.customColors.push({ name: this.currentConsumptionName, value: this.primaryRgbColor });
	}

	private getCurrentMonth(): string {
		return this.readingDate.locale(this.translocoLocaleService.getLocale()).format('MMMM');
	}

	getLastMonth(): string {
		const dateMinusMonth = dayjs(this.readingDate).subtract(1, 'month').toDate();
		return dateMinusMonth.toLocaleString(this.translocoLocaleService.getLocale(), { month: 'long' });
	}

	getCurrentYear(): string {
		return dayjs(this.readingDate).format('YYYY');
	}

	getLastYear(): string {
		const dateMinusYear = dayjs(this.readingDate).subtract(1, 'year').toDate();
		return dateMinusYear.toLocaleString(this.translocoLocaleService.getLocale(), { year: 'numeric' });
	}

	yAxisTickFormatting(value: any) {
		return value;
	}

	setBarStyleByDevice(bar: string) {
		if (this.layoutService.isMobile()) {
			return 'dataTextLabel ' + bar + 'Mobile';
		} else if (this.layoutService.isTablet()) {
			return 'dataTextLabel ' + bar + 'Tablet';
		} else if (this.layoutService.isTabletLandscape()) {
			return 'dataTextLabel ' + bar + 'TabletLandscape';
		} else {
			return 'dataTextLabel ' + bar + 'Desktop';
		}
	}

	getTitleByCostType(energyValue: any): string {
		return CostTypeEnum.WARMWATER !== energyValue.costType
			? this.translocoService.translate('uvi.averageConsumption')
			: energyValue.averageDinCalculation
				? this.translocoService.translate('uvi.averageConsumptionWwNewDin')
				: this.translocoService.translate('uvi.averageConsumptionWw');
	}

	formatDataLabelCubic(value: number) {
		if (value !== undefined) {
			const activeLang = this.translocoService.getActiveLang();
			if (activeLang === 'de') {
				const replacedSeperator = value.toString().replace('.', ',');
				return replacedSeperator + ' m³';
			}
			return value + ' m³';
		} else {
			return '';
		}
	}

	private setSizeIfMobile() {
		this.isMobile = this.layoutService.isMobile() || this.layoutService.isLtLg();

		if (this.layoutService.isMobile()) {
			const width = window.innerWidth * 0.7;
			const height = width * 0.8;
			this.diagramSize = [width, height];
		} else {
			this.diagramSize = [550, 250];
		}
	}

	private fillChartDataArray() {
		const currentMonth = this.getCurrentMonth();
		const lastMonth = this.getLastMonth();
		const lastYear = this.getLastYear();
		const currentYear = this.getCurrentYear();
		this.lastYearConsumptionName = currentMonth + ' ' + lastYear;
		this.lastMonthConsumptionName = lastMonth.startsWith('D') ? lastMonth + ' ' + lastYear : lastMonth + ' ' + currentYear;
		this.currentConsumptionName = currentMonth + ' ' + currentYear;
		const consumption = this.consumption();
		const consumptionValue = this.consumption();
		const consumptionVal = this.consumption();
		return [
			{
				name: this.lastYearConsumptionName,
				value:
					consumption.costType === this.CostTypeEnum.COLDWATER
						? (consumption.lastYearConsumptionPeriod.totalConsumption ?? 0.0)
						: (consumption.lastYearConsumptionPeriod.totalConsumptionAsKwh ?? 0.0),
			},
			{
				name: this.lastMonthConsumptionName,
				value:
					consumptionValue.costType === this.CostTypeEnum.COLDWATER
						? (consumptionValue.lastMonthConsumptionPeriod.totalConsumption ?? 0.0)
						: (consumptionValue.lastMonthConsumptionPeriod.totalConsumptionAsKwh ?? 0.0),
			},
			{
				name: this.currentConsumptionName,
				value:
					consumptionVal.costType === this.CostTypeEnum.COLDWATER
						? (consumptionVal.currentConsumptionPeriod.totalConsumption ?? 0)
						: (consumptionVal.currentConsumptionPeriod.totalConsumptionAsKwh ?? 0.0),
			},
		];
	}

	getChartDataByCostType(costType: CostTypeEnum) {
		switch (costType) {
			case CostTypeEnum.HEATING:
				return this.chartDataHeating;
			case CostTypeEnum.WARMWATER:
				return this.chartDataWarmwater;
			case CostTypeEnum.COLDWATER:
				return this.chartDataColdwater;
			case CostTypeEnum.COOLING:
				return this.chartDataCooling;
			case CostTypeEnum.HEATING_WARMWATER:
				return this.chartDataHeatingWarmwater;
		}
	}
}
